/**
 * IntegratrContext.tsx - React Context for Integratr
 * 
 * CHANGE HISTORY:
 * - 251120_1930 [pending] danielbeer: Initial creation - Phase 1 critical orchestrator
 * 
 * Provides Integratr functionality to React components.
 */

import React, { createContext, useContext, useState, useMemo, ReactNode } from 'react';
import { IntegratrManager } from './core/IntegratrManager';
import type { IntegratrConfig } from './types';

/**
 * Context value interface
 */
export interface IntegratrContextValue {
  manager: IntegratrManager;
  
  // API methods will be added as needed
  // makeAPICall: (config: APICallConfig) => Promise<APIResponse>;
  // registerWebhook: (config: WebhookConfig) => Promise<string>;
  // etc.
}

const IntegratrContext = createContext<IntegratrContextValue | null>(null);

export interface IntegratrProviderProps {
  children: ReactNode;
  actr?: any;
  loggr?: any;
  profylr?: any;
  config?: IntegratrConfig;
}

/**
 * IntegratrProvider - Provides Integratr context to children
 * 
 * Wrap your app or component tree with this provider to use Integratr hooks.
 * 
 * @example
 * ```tsx
 * <IntegratrProvider actr={actr} loggr={loggr}>
 *   <YourApp />
 * </IntegratrProvider>
 * ```
 */
export const IntegratrProvider: React.FC<IntegratrProviderProps> = ({
  children,
  actr,
  loggr,
  profylr,
  config = {}
}) => {
  // Initialize manager (stable across renders)
  const [manager] = useState(() => {
    const instance = new IntegratrManager(config);
    // Inject dependencies
    if (actr) instance['actr'] = actr;
    if (loggr) instance['loggr'] = loggr;
    if (profylr) instance['profylr'] = profylr;
    return instance;
  });
  
  // CRITICAL: Always wrap context value in useMemo to prevent infinite re-renders
  const value = useMemo<IntegratrContextValue>(() => ({
    manager,
  }), [manager]);
  
  return (
    <IntegratrContext.Provider value={value}>
      {children}
    </IntegratrContext.Provider>
  );
};

/**
 * useIntegratr - Hook to access Integratr context
 * 
 * @throws Error if used outside IntegratrProvider
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { manager } = useIntegratr();
 *   
 *   const callAPI = async () => {
 *     const response = await manager.makeAPICall({
 *       url: 'https://api.example.com/data',
 *       method: 'GET'
 *     });
 *     console.log(response);
 *   };
 *   
 *   return <button onClick={callAPI}>Call API</button>;
 * }
 * ```
 */
export function useIntegratr(): IntegratrContextValue {
  const context = useContext(IntegratrContext);
  
  if (!context) {
    throw new Error('useIntegratr must be used within IntegratrProvider');
  }
  
  return context;
}

export default IntegratrContext;
